import { parseOrThrow } from "_shared/_utilities/zod-schema-parser";
import Guid from "_shared/guids/guid";
import BookmarkSchema from "marketplace/bookmarks/_schema/bookmark-schema";
import { z } from "zod";

export type BookmarkedTeamJSON = z.infer<typeof BookmarkedTeamSchema.BookmarkedTeamJSONZ>;
export type BookmarkedTeamParams = z.infer<typeof BookmarkedTeamSchema.BookmarkedTeamZ>;

export default class BookmarkedTeamSchema {
  public static readonly BookmarkedTeamJSONZ = z.object({
    ...BookmarkSchema.BookmarkJSONZ.shape,
    teamId: z.uuid(),
  });
  public static readonly BookmarkedTeamZ = z.object({
    ...BookmarkSchema.BookmarkZ.shape,
    teamId: z.instanceof(Guid),
  });

  public static validate(values: unknown): BookmarkedTeamParams {
    return parseOrThrow(BookmarkedTeamSchema.BookmarkedTeamZ, values, {
      logError: true,
      schemaName: "BookmarkedTeamSchema",
      operation: "validate",
      payloadSample: values,
    });
  }

  public static validateJSON(values: unknown): BookmarkedTeamJSON {
    return parseOrThrow(BookmarkedTeamSchema.BookmarkedTeamJSONZ, values, {
      logError: true,
      schemaName: "BookmarkedTeamSchema",
      operation: "validateJSON",
      payloadSample: values,
    });
  }

  public static validateJSONArray(values: unknown): BookmarkedTeamJSON[] {
    return parseOrThrow(z.array(BookmarkedTeamSchema.BookmarkedTeamJSONZ), values, {
      logError: true,
      schemaName: "BookmarkedTeamSchema",
      operation: "validateJSONArray",
      payloadSample: values,
    });
  }

  public static isValid(values: unknown): values is BookmarkedTeamParams {
    try {
      BookmarkedTeamSchema.validate(values);
      return true;
    } catch {
      return false;
    }
  }

  public static isValidJSON(values: unknown): values is BookmarkedTeamJSON {
    try {
      BookmarkedTeamSchema.validateJSON(values);
      return true;
    } catch {
      return false;
    }
  }

  public static deserialize(data: BookmarkedTeamJSON): BookmarkedTeamParams {
    const parsed = parseOrThrow(BookmarkedTeamSchema.BookmarkedTeamJSONZ, data, {
      logError: true,
      schemaName: "BookmarkedTeamSchema",
      operation: "deserialize",
      payloadSample: data,
    });
    return BookmarkedTeamSchema.codec.decode(parsed);
  }

  public static deserializeArray(data: BookmarkedTeamJSON[]): BookmarkedTeamParams[] {
    return data.map((item) => BookmarkedTeamSchema.deserialize(item));
  }

  public static serialize(document: BookmarkedTeamParams): BookmarkedTeamJSON {
    const parsed = BookmarkedTeamSchema.validate(document);
    return BookmarkedTeamSchema.codec.encode(parsed);
  }

  public static serializeArray(documents: BookmarkedTeamParams[]): BookmarkedTeamJSON[] {
    return documents.map((item) => BookmarkedTeamSchema.serialize(item));
  }

  private static readonly codec = z.codec(
    BookmarkedTeamSchema.BookmarkedTeamJSONZ,
    BookmarkedTeamSchema.BookmarkedTeamZ,
    {
      decode: (data) => ({
        ...BookmarkSchema.deserialize(data),
        teamId: Guid.fromJSON(data.teamId)!,
      }),
      encode: (bookmark) => ({
        ...BookmarkSchema.serialize(bookmark),
        teamId: bookmark.teamId.toJSON(),
      }),
    }
  );
}
